import { create } from 'zustand';
import { fetchQuery } from '@/utils/fetchQuery';

export interface AppLinkItem {
    image?: {
        sourceUrl?: string;
        altText?: string;
        title?: string;
        mediaDetails?: {
            width?: number;
            height?: number;
        };
    };
    link?: {
        url?: string;
        target?: string;
        title?: string;
    };
}

export interface AppSidebarData {
    content?: string;
    appLogo?: {
        sourceUrl?: string;
        altText?: string;
        title?: string;
    };
    banner?: {
        desktop?: {
            sourceUrl?: string;
            altText?: string;
            mediaDetails?: { width?: number; height?: number };
        };
        mobile?: {
            sourceUrl?: string;
            altText?: string;
            mediaDetails?: { width?: number; height?: number };
        };
    };
    appLinks?: AppLinkItem[];
}

interface AppSidebarStore {
    isOpen: boolean;
    data: AppSidebarData | null;
    isLoading: boolean;
    error: string | null;
    openSidebar: () => void;
    closeSidebar: () => void;
    setIsOpen: (isOpen: boolean) => void;
    fetchSidebarData: () => Promise<void>;
    prefetchSidebarData: () => void;
}

const STORAGE_KEY = 'tether_app_sidebar_cache_v1';

// Instant Fallback Data: Zero network delay, zero skeleton lag, guarantees instant UI presentation
const DEFAULT_SIDEBAR_DATA: AppSidebarData = {
    content:
        '<p>با اپلیکیشن تترچند میتوانید تتر و سایر رمزارز ها را با ارزان‌ترین قیمت ممکن در بهترین زمان ممکن خریداری کنید و در صرافی دیگر با بالاترین قیمت بفروشید.</p>',
    appLogo: {
        sourceUrl:
            'https://panel.khanesarmaye.com/wp-content/uploads/2026/07/Frame-1261153341.webp',
        altText: 'تتر چند',
        title: 'تتر چند',
    },
    banner: {
        desktop: {
            sourceUrl:
                'https://panel.khanesarmaye.com/wp-content/uploads/2026/09/ChatGPT-Image-Sep-30-2026-12_54_07-PM.png',
            altText: 'اپلیکیشن تترچند',
            mediaDetails: { width: 1448, height: 1086 },
        },
        mobile: {
            sourceUrl:
                'https://panel.khanesarmaye.com/wp-content/uploads/2026/09/ChatGPT-Image-Sep-30-2026-12_54_18-PM.png',
            altText: 'اپلیکیشن تترچند',
            mediaDetails: { width: 1774, height: 887 },
        },
    },
    appLinks: [
        {
            image: {
                sourceUrl:
                    'https://panel.khanesarmaye.com/wp-content/uploads/2026/09/bazar-logo.webp',
                altText: 'دریافت از بازار',
                title: 'دریافت از بازار',
                mediaDetails: { width: 421, height: 125 },
            },
            link: {
                url: 'https://trc.metrix.ir/lygs97/',
                target: '_blank',
                title: '',
            },
        },
        {
            image: {
                sourceUrl:
                    'https://panel.khanesarmaye.com/wp-content/uploads/2026/09/myket-logo.webp',
                altText: 'دریافت از مایکت',
                title: 'دریافت از مایکت',
                mediaDetails: { width: 421, height: 121 },
            },
            link: {
                url: 'https://myket.ir/app/com.khanesarmaye.app.tether',
                target: '_blank',
                title: '',
            },
        },
        {
            image: {
                sourceUrl:
                    'https://panel.khanesarmaye.com/wp-content/uploads/2025/10/web-app-1.png',
                altText: 'وب اپلیکیشن',
                title: 'وب اپلیکیشن',
                mediaDetails: { width: 224, height: 66 },
            },
            link: {
                url: 'https://app.khanesarmaye.com/home',
                target: '_blank',
                title: '',
            },
        },
        {
            image: {
                sourceUrl:
                    'https://panel.khanesarmaye.com/wp-content/uploads/2026/08/play-store.webp',
                altText: 'دریافت از گوگل‌پلی',
                title: 'دریافت از گوگل‌پلی',
                mediaDetails: { width: 1024, height: 304 },
            },
            link: {
                url: 'https://play.google.com/store/apps/details?id=com.khanesarmaye.app.tether&pcampaignid=web_share',
                target: '',
                title: '',
            },
        },
    ],
};

const getInitialData = (): AppSidebarData => {
    if (typeof window !== 'undefined') {
        try {
            const cached = localStorage.getItem(STORAGE_KEY);
            if (cached) {
                const parsed = JSON.parse(cached);
                if (parsed && typeof parsed === 'object') {
                    return parsed;
                }
            }
        } catch {
            // ignore localStorage read error
        }
    }
    return DEFAULT_SIDEBAR_DATA;
};

const TETHER_SIDEBAR_QUERY = `
query GetTetherAppSidebar {
    pageBy(pageId: 142840) {
        app {
            appLogo {
                sourceUrl
                altText
                title
            }
            tethersidebar {
                content
                banner {
                    desktop {
                        sourceUrl
                        altText
                        mediaDetails {
                            width
                            height
                        }
                    }
                    mobile {
                        sourceUrl
                        altText
                        mediaDetails {
                            width
                            height
                        }
                    }
                }
            }
            appLinks {
                image {
                    sourceUrl
                    altText
                    title
                    mediaDetails {
                        width
                        height
                    }
                }
                link {
                    url
                    target
                    title
                }
            }
        }
    }
}
`;

export const useAppSidebarStore = create<AppSidebarStore>((set, get) => ({
    isOpen: false,
    data: getInitialData(),
    isLoading: false,
    error: null,
    openSidebar: () => {
        set({ isOpen: true });
        // Background refresh only when opening if not already loading
        if (!get().isLoading) {
            get().fetchSidebarData();
        }
    },
    closeSidebar: () => set({ isOpen: false }),
    setIsOpen: (isOpen: boolean) => {
        set({ isOpen });
        if (isOpen && !get().isLoading) {
            get().fetchSidebarData();
        }
    },
    prefetchSidebarData: () => {
        if (!get().isLoading) {
            get().fetchSidebarData();
        }
    },
    fetchSidebarData: async () => {
        // Only set isLoading if data is somehow empty
        if (!get().data) {
            set({ isLoading: true, error: null });
        }
        try {
            const res = await fetchQuery<{ pageBy?: { app?: any } }>(TETHER_SIDEBAR_QUERY, {}, 60);
            const app = res?.pageBy?.app;
            const tethersidebar = app?.tethersidebar;
            const appLinks = app?.appLinks;
            const appLogo = app?.appLogo;

            const formatted: AppSidebarData = {
                content: tethersidebar?.content || DEFAULT_SIDEBAR_DATA.content,
                appLogo: appLogo || DEFAULT_SIDEBAR_DATA.appLogo,
                banner: tethersidebar?.banner || DEFAULT_SIDEBAR_DATA.banner,
                appLinks:
                    Array.isArray(appLinks) && appLinks.length > 0
                        ? appLinks
                        : DEFAULT_SIDEBAR_DATA.appLinks,
            };

            set({
                data: formatted,
                isLoading: false,
            });

            if (typeof window !== 'undefined') {
                try {
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(formatted));
                } catch {
                    // ignore write error
                }
            }
        } catch (err: any) {
            console.error('Failed to fetch tether sidebar data:', err);
            set({
                error: err?.message || 'Error loading sidebar data',
                isLoading: false,
            });
        }
    },
}));
